import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../Components/Navbar/Navbar';
import Footer from '../../Components/Footer/Footer';
import SEO from '../../Components/SEO/SEO';
import BlogArt from './BlogArt';
import styles from './blog.module.css';
import { postsByDate, postsInCategory, activeCategories } from '../../blog/posts';
import { BLOG_INDEX_META, BLOG_CATEGORY_META } from '../../seo/pageMeta';
import { SITE_ORIGIN } from '../../utils/locale';

/**
 * The journal index, and each category page.
 *
 * One component for both, because a category page IS the index filtered — two
 * components would be two places for the card markup to drift. It carries an
 * ItemList so an answer engine can read what is here without rendering, and
 * the category chips are real links, so the whole section is crawlable by
 * following them rather than only from the sitemap.
 */
export default function BlogIndex({ category = null }) {
  const { t, i18n } = useTranslation();
  const posts = category ? postsInCategory(category) : postsByDate();
  const categories = activeCategories();
  const meta = category ? (BLOG_CATEGORY_META[category] || {}) : BLOG_INDEX_META;
  const canonical = category ? `/blog/category/${category}` : '/blog';
  const prefix = i18n.language === 'en' ? '' : `/${i18n.language}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: category ? t(`blog.categories.${category}.name`) : t('blog.index.title'),
      inLanguage: i18n.language,
      itemListElement: posts.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${SITE_ORIGIN}${prefix}/blog/${p.slug}`,
        name: t(`blog.posts.${p.slug}.title`),
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Stylon', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: t('blog.index.title'), item: `${SITE_ORIGIN}/blog` },
        ...(category
          ? [{ '@type': 'ListItem', position: 3, name: t(`blog.categories.${category}.name`) }]
          : []),
      ],
    },
  ];

  return (
    <>
      <SEO title={meta.title} description={meta.description} canonical={canonical} jsonLd={jsonLd} />
      <Navbar />
      <main className={`${styles.wrap} ${styles.wide}`}>
        <h1 className={styles.title}>
          {category ? t(`blog.categories.${category}.name`) : t('blog.index.title')}
        </h1>
        <p className={styles.lede}>
          {category ? t(`blog.categories.${category}.blurb`) : t('blog.index.subtitle')}
        </p>

        <nav className={styles.chips} aria-label={t('blog.index.browseBy')}>
          <Link to="/blog" className={`${styles.chip} ${category ? '' : styles.chipOn}`}>
            {t('blog.index.allPosts')}
          </Link>
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/blog/category/${c.slug}`}
              className={`${styles.chip} ${category === c.slug ? styles.chipOn : ''}`}
            >
              {t(`blog.categories.${c.slug}.name`)}
            </Link>
          ))}
        </nav>

        <div className={styles.grid}>
          {posts.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className={styles.card}>
              <BlogArt slug={p.slug} />
              <div className={styles.cardBody}>
                <p className={styles.cardCat}>{t(`blog.categories.${p.category}.name`)}</p>
                <h2 className={styles.cardTitle}>{t(`blog.posts.${p.slug}.title`)}</h2>
                <p className={styles.cardExcerpt}>{t(`blog.posts.${p.slug}.excerpt`)}</p>
              </div>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
}
